import querystring from 'query-string';
import { withFormik } from 'formik';
import Cookies from 'js-cookie';
import {
    validateRequire,
    validateEmail,
    validateLength,
    validatePhoneNumber,
    validateDate
} from 'client/components/FormContent/FieldList/FormDataManage/formValidate';
import updateProgress from 'client/components/FormContent/FieldList/FormDataManage/formStatus';
import { postForm, newPostForm, clientUploadFile } from 'api/client';
import { gtmPushUserSubmit } from 'common/utils/tracking';
import { getUtmQueryString } from 'common/utils/getQueryString';
import { generateFbcByFbclid } from 'common/utils/tracking';
import { OPTIONS_TYPE } from 'common/utils/config';
import { isNonEmptyObject } from 'common/utils/objectManipulate';

export default function EnhanceForm(Comp) {
    return withFormik({
        mapPropsToValues: props => {
            const { fieldDataList } = props;
            const data = fieldDataList.reduce((obj, elem) => {
                const { id } = elem;
                obj[id] = '';
                return obj;
            }, {});
            return data;
        },

        validate: (values, props) => {
            const { t, fieldDataList, setProgress } = props;
            let errors = fieldDataList.reduce((obj, elem) => {
                const { id } = elem;
                const val = values[id];
                const validator = {
                    101: () => validateLength(val),
                    102: () => validateLength(val) || validateEmail(val),
                    103: () => validateLength(val) || validatePhoneNumber(val),
                    104: () => validateLength(val),
                    401: () => validateDate(val),
                    default: () => ''
                };

                const message = (validator[elem.type] || validator['default'])();

                if (message) {
                    obj[id] = t(message);
                }

                if (elem.isRequired) {
                    const result = validateRequire(val, 'label');
                    if (result) {
                        obj[id] = t(result);
                    }
                }

                return obj;
            }, {});

            updateProgress(fieldDataList, values, errors, setProgress);
            return errors;
        },

        handleSubmit: async (values, { setSubmitting, setErrors, setFieldValue, props }) => {
            const {
                t,
                formId,
                sponsorId,
                formVersion,
                history,
                trackField,
                routeSponsor,
                fieldDataList,
                onError,
                onErrorMsg,
                hiddenFieldsValue,
                submitPostMessage = false,
                isInFrame,
                promoLoginToken,
                promoSerialNo,
                sortedFieldIds,
                formStatus,
                options,
                refetch
            } = props;

            try {
                const failedUploadFiles = {};
                const successUploadFiles = [];
                const formData = {};
                await Promise.all(
                    fieldDataList.map(async ({ id: fieldId }) => {
                        let fieldValue = values[fieldId];
                        if (fieldValue instanceof File) {
                            try {
                                const resp = (await clientUploadFile(fieldValue)) || {};
                                if (resp.code !== 200) {
                                    throw new Error(resp.message);
                                }
                                const { items: { filename } = {} } = resp;
                                fieldValue = filename;
                                successUploadFiles.push({ fieldId, filename });
                            } catch (error) {
                                failedUploadFiles[fieldId] = t('form.submit_upload_file_failed');
                            }
                        }
                        if (Array.isArray(fieldValue) && fieldValue.length === 0) {
                            fieldValue = '';
                        }
                        formData[fieldId] = { fieldId, fieldValue };
                    })
                );
                if (Object.keys(failedUploadFiles).length > 0) {
                    successUploadFiles.forEach(({ fieldId, filename }) =>
                        setFieldValue(fieldId, filename, false)
                    );
                    onError(failedUploadFiles);
                    setSubmitting(false);
                    return setErrors(failedUploadFiles);
                }

                const {
                    utm_source,
                    utm_medium,
                    utm_campaign,
                    utm_term,
                    utm_content
                } = getUtmQueryString(history.location.search, trackField);
                // GA 追蹤 user 的 cookie 儲存在 _ga,  一般稱為 client_id
                const gaCookie = Cookies.get('_ga');
                const fbp = Cookies.get('_fbp');
                const fbc = Cookies.get('_fbc');
                const { fbclid } = querystring.parse(window.location.search);
                const isGAExist = gaCookie !== undefined;

                const data = {
                    data: [
                        ...sortedFieldIds.reduce((array, fid) => {
                            const fieldVal = formData[fid];
                            const newFieldVal = handleFieldValue(fieldVal, options);

                            if (newFieldVal) {
                                array.push(newFieldVal);
                            }
                            return array;
                        }, []),
                        ...hiddenFieldsValue
                    ],
                    trackCode: {
                        // gaClientId: GA1.1.1626056991.1692173999 -> 1626056991.1692173999
                        // Ref: https://app.clickup.com/36226806/docs/12hhqp-8660/12hhqp-20080
                        gaClientId: isGAExist ? gaCookie.substring(6) : '',
                        utmSource: utm_source,
                        utmCampaign: utm_campaign,
                        utmMedium: utm_medium,
                        utmTerm: utm_term,
                        utmContent: utm_content,
                        referralUrl: window.document.referrer,
                        clientUserAgent: navigator.userAgent
                    },
                    serialNumber: promoSerialNo
                };

                if (fbp) {
                    data.trackCode = {
                        ...data.trackCode,
                        fbp
                    };
                }

                if (fbclid || fbc) {
                    const value = fbclid ? generateFbcByFbclid(fbclid) ?? fbc : fbc;
                    if (fbclid) {
                        Cookies.set('_fbp', value);
                    }
                    data.trackCode = {
                        ...data.trackCode,
                        fbc: value
                    };
                }

                const recaptchaToken = values['recaptchaToken'];
                const result = await (formVersion === 0
                    ? postForm(
                          formId,
                          data,
                          recaptchaToken,
                          submitPostMessage ? promoLoginToken : undefined
                      )
                    : newPostForm(formId, data, recaptchaToken, formStatus));
                if (result.code === 200) {
                    const { memberId, memberUuid } = result.items || {};
                    if ((memberId || memberUuid) && !submitPostMessage) {
                        gtmPushUserSubmit({
                            memberId: memberId || memberUuid,
                            formId,
                            sponsorId
                        });
                    }
                    if (submitPostMessage && isInFrame) {
                        window.parent.postMessage({ submitted: true, memberId }, '*');
                    } else {
                        history.push(
                            `/${routeSponsor}/${formId}/response${window.location.search}`
                        );
                    }
                } else if (result.code === 226) {
                    if (result.message === '1-1003-0000-12') {
                        const errorFieldList = getErrorHintObj(
                            result.errors,
                            t('form.field_answer_duplicate')
                        );
                        onError(errorFieldList);
                        setErrors(errorFieldList);
                    } else {
                        onErrorMsg({
                            title: t('system.except_error'),
                            code: result.code,
                            msg: result.message
                        });
                    }
                } else if (result.code === 422 && result.message === '2-1003-0001-21') {
                    // https://app.clickup.com/t/86etjd8pu 判斷是選項問題
                    if (result.errors.length === 3 && result.errors[1] === '5-1003-0001-31') {
                        const errorFieldId = result.errors[2][2];
                        const errorOptionId = result.errors[2][3];
                        const errorOptionObj = getErrorHintObj(
                            {
                                [errorFieldId]: [errorOptionId]
                            },
                            t('label.please_reselect')
                        );
                        setErrors(errorOptionObj);
                        // 觸發 refetch 並啟動重設邏輯
                        refetch(formId);
                    } else {
                        onErrorMsg({
                            title: t('system.duplicate_form_error'),
                            code: result.code,
                            msg: result.message
                        });
                        window.parent.postMessage({ submitted: false }, '*');
                    }
                } else if (result.code === 400) {
                    let errorFieldList;
                    // 題目必填
                    if (result.message === '1-1003-0000-21') {
                        errorFieldList = getErrorHintObj(result.errors, t('form.field_must_fill'));
                    }
                    // 資料格式不符
                    if (result.message === '1-1003-0000-22') {
                        errorFieldList = getErrorHintObj(
                            result.errors,
                            t('form.field_format_error')
                        );
                    }
                    onError(errorFieldList);
                    setErrors(errorFieldList);
                } else {
                    onErrorMsg({
                        title: t('system.except_error'),
                        code: result.code,
                        msg: result.message
                    });
                }
                setSubmitting(false);
            } catch (error) {
                onErrorMsg({
                    title: t('system.except_error'),
                    code: 500,
                    msg: error.message
                });
            }
        },

        displayName: 'BasicForm'
    })(Comp);
}

/**
 *
 * @param {*} errors 錯誤參數 (有兩種)
 * @param {*} message 錯誤訊息
 * @returns {Object} 錯誤物件  (有兩種)
 */
function getErrorHintObj(errors, message) {
    if (Array.isArray(errors)) {
        return errors.reduce((obj, fieldId) => {
            obj[fieldId] = message;
            return obj;
        }, {});
    }

    if (isNonEmptyObject(errors)) {
        return Object.entries(errors).reduce((obj, [fieldId, optionId]) => {
            obj[fieldId] = {
                optionId: optionId,
                message: message
            };
            return obj;
        }, {});
    }
}

const handleFieldValue = (fieldData, options) => {
    const processItem = item => {
        const option = options.find(opt => opt.id === item.optionId);
        return option?.type === OPTIONS_TYPE.NORMAL_OTHER
            ? {
                  ...item,
                  label: `${option.label}-${item.label}`
              }
            : item;
    };

    // 處理 fieldValue
    const processedFieldValue = Array.isArray(fieldData.fieldValue)
        ? fieldData.fieldValue.map(processItem)
        : fieldData.fieldValue && typeof fieldData.fieldValue === 'object'
        ? processItem(fieldData.fieldValue)
        : fieldData.fieldValue;

    // 返回處理後的完整資料結構
    return {
        ...fieldData,
        fieldValue: processedFieldValue
    };
};
